import React, { useCallback, useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AvgMonthlyBusinessIncomeInputProps {
  value?: Record<string, any>;
  onChange?: (data: Record<string, any>) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

const incomeRanges = [
  { value: '<50000', label: 'Less than ₹50,000' },
  { value: '50000-100000', label: '₹50,000 - ₹1,00,000' },
  { value: '100000-200000', label: '₹1,00,000 - ₹2,00,000' },
  { value: '200000-300000', label: '₹2,00,000 - ₹3,00,000' },
  { value: '300000-400000', label: '₹3,00,000 - ₹4,00,000' },
  { value: '400000-500000', label: '₹4,00,000 - ₹5,00,000' },
  { value: '500000-600000', label: '₹5,00,000 - ₹6,00,000' },
  { value: '600000-700000', label: '₹6,00,000 - ₹7,00,000' },
  { value: '700000-800000', label: '₹7,00,000 - ₹8,00,000' },
  { value: '800000-900000', label: '₹8,00,000 - ₹9,00,000' },
  { value: '900000-1000000', label: '₹9,00,000 - ₹10,00,000' },
];

const AvgMonthlyBusinessIncomeInput: React.FC<AvgMonthlyBusinessIncomeInputProps> = ({
  value = {},
  onChange,
  disabled = false,
  registerSubmit,
}) => {
  const currentValue = value?.avg_monthly_business_income_range ?? '';
  const [validationError, setValidationError] = useState<string>('');
  const [touched, setTouched] = useState(false);

  const handleChange = useCallback((selectedValue: string) => {
    setTouched(true);
    setValidationError(''); // Clear error when user selects a value
    onChange?.({ avg_monthly_business_income_range: selectedValue });
  }, [onChange]);

  // Register validation handler with JourneyPage
  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        setTouched(true);
        if (!currentValue || currentValue.trim() === '') {
          setValidationError('Please select your average monthly business income');
          return false;
        }
        setValidationError('');
        return true;
      });
    }
  }, [registerSubmit, currentValue]);

  // Show validation error if field is touched and empty
  useEffect(() => {
    if (touched && (!currentValue || currentValue.trim() === '')) {
      setValidationError('Please select your average monthly business income');
    } else if (touched && currentValue) {
      setValidationError('');
    }
  }, [touched, currentValue]);

  return (
    <div className="space-y-1.5">
      <Label htmlFor="avg_monthly_business_income_range" className="text-sm font-medium text-gray-700">
        Average Monthly Business Income <span className="text-red-500">*</span>
      </Label>
      <Select
        value={currentValue}
        onValueChange={handleChange}
        disabled={disabled}
      >
        <SelectTrigger
          id="avg_monthly_business_income_range"
          className={`mt-1 ${validationError ? 'border-red-500' : ''}`}
        >
          <SelectValue placeholder="Select income range" />
        </SelectTrigger>
        <SelectContent>
          {incomeRanges.map((range) => (
            <SelectItem key={range.value} value={range.value}>
              {range.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {validationError ? (
        <p className="text-xs text-red-500 mt-1">{validationError}</p>
      ) : (
        <p className="text-xs text-gray-500 mt-1">
          Select your average monthly business income range
        </p>
      )}
    </div>
  );
};

export default AvgMonthlyBusinessIncomeInput;
